'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast, Toaster } from 'sonner';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

interface LoginFormValues {
  email: string;
  password: string;
  remember: boolean;
}

interface DemoCredential {
  role: string;
  email: string;
  password: string;
  badge: 'manager' | 'staff';
  description: string;
}

// BACKEND INTEGRATION: Replace with real auth API call
const DEMO_CREDENTIALS: DemoCredential[] = [
  {
    role: 'Accounts Manager',
    email: 'priya.sharma@mehtaindustries.in',
    password: 'TallyMgr@2026',
    badge: 'manager',
    description: 'Full P&L, ledgers, all metrics',
  },
  {
    role: 'Accounts Staff',
    email: 'rajesh.kumar@mehtaindustries.in',
    password: 'TallyStaff@2026',
    badge: 'staff',
    description: 'AR/AP view, no P&L details',
  },
];

const VALID_CREDENTIALS = new Set(DEMO_CREDENTIALS.map(c => `${c.email}:${c.password}`));

export default function LoginShell() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormValues>({
    defaultValues: { email: '', password: '', remember: false },
  });

  const onSubmit = (data: LoginFormValues) => {
    setIsLoading(true);
    setAuthError('');
    // BACKEND INTEGRATION: POST /api/auth/login with { email, password }
    setTimeout(() => {
      const key = `${data.email}:${data.password}`;
      if (VALID_CREDENTIALS.has(key)) {
        toast.success('Signed in successfully', {
          description: `Welcome back, ${data.email.split('.')[0].charAt(0).toUpperCase() + data.email.split('.')[0].slice(1)}`,
        });
        setTimeout(() => router.push('/financial-dashboard'), 800);
      } else {
        setAuthError('Invalid credentials — use the demo accounts below to sign in');
        setIsLoading(false);
      }
    }, 1400);
  };

  const autofill = (cred: DemoCredential) => {
    setValue('email', cred.email);
    setValue('password', cred.password);
    setAuthError('');
  };

  const financialStats = [
    { label: 'Companies Connected', value: '3', icon: 'BuildingOffice2Icon' },
    { label: 'Ledgers Synced', value: '1,284', icon: 'BookOpenIcon' },
    { label: 'Vouchers Processed', value: '48,620', icon: 'DocumentTextIcon' },
    { label: 'Data Points', value: '2.4M', icon: 'ChartBarIcon' },
  ];

  return (
    <div className="min-h-screen flex bg-slate-50">
      <Toaster position="bottom-right" richColors />

      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] bg-gradient-to-br from-blue-800 via-blue-700 to-blue-900 flex-col justify-between p-10 xl:p-14 relative overflow-hidden">
        {/* Background decorative circles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5" />
          <div className="absolute top-1/3 -left-16 w-64 h-64 rounded-full bg-white/5" />
          <div className="absolute -bottom-16 right-1/4 w-80 h-80 rounded-full bg-blue-600/40" />
          <div className="absolute top-1/2 right-1/3 w-40 h-40 rounded-full bg-blue-500/20" />
        </div>

        {/* Logo */}
        <div className="relative flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <AppLogo size={28} />
          </div>
          <span className="text-white font-bold text-xl tracking-tight">TallyDash</span>
        </div>

        {/* Main copy */}
        <div className="relative">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1.5 mb-6">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-white/80 text-xs font-medium">Live Tally ERP Integration</span>
          </div>
          <h2 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-4">
            Your Tally data,<br />
            visualised in real-time
          </h2>
          <p className="text-blue-200 text-base leading-relaxed max-w-sm">
            P&L summaries, AR/AP aging, cost centre performance and ledger analytics — all synced automatically from Tally ERP every morning.
          </p>

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-3 mt-8">
            {financialStats.map((stat) => (
              <div key={`stat-${stat.label}`} className="bg-white/10 border border-white/15 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={16} className="text-blue-300" />
                  <span className="text-blue-300 text-xs font-medium">{stat.label}</span>
                </div>
                <p className="text-white text-xl font-bold font-mono tabular-nums">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative flex items-center justify-between">
          <p className="text-blue-300 text-xs">Mehta Industries Pvt Ltd · Tally ERP 9 / Prime</p>
          <div className="flex items-center gap-1.5 text-xs text-blue-300">
            <Icon name="ShieldCheckIcon" size={13} />
            <span>SOC 2 Compliant</span>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-[440px]">

          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-2.5 mb-8">
            <AppLogo size={32} />
            <span className="font-bold text-slate-900 text-xl tracking-tight">TallyDash</span>
          </div>

          <div className="mb-7">
            <h1 className="text-2xl font-bold text-slate-900 mb-1.5">Sign in to your account</h1>
            <p className="text-sm text-slate-500">Access your Tally ERP financial dashboard</p>
          </div>

          {/* Auth error */}
          {authError && (
            <div className="mb-4 flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <Icon name="ExclamationCircleIcon" size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700 font-medium">{authError}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-1.5">
                Work Email
              </label>
              <p className="text-xs text-slate-400 mb-1.5">Use your company email registered with Tally</p>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="priya.sharma@company.in"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Enter a valid email address',
                  },
                })}
                className={`input-field ${errors.email ? 'border-red-400 focus:ring-red-400' : ''}`}
              />
              {errors.email && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={12} />
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="block text-sm font-semibold text-slate-700">
                  Password
                </label>
                <button type="button" className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors">
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 6, message: 'Password must be at least 6 characters' },
                  })}
                  className={`input-field pr-10 ${errors.password ? 'border-red-400 focus:ring-red-400' : ''}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={12} />
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Remember me */}
            <div className="flex items-center gap-2.5">
              <input
                id="remember"
                type="checkbox"
                {...register('remember')}
                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="remember" className="text-sm text-slate-600 cursor-pointer select-none">
                Keep me signed in for 30 days
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full flex items-center justify-center gap-2 py-2.5 text-sm"
              style={{ minHeight: '42px' }}
            >
              {isLoading ? (
                <>
                  <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <Icon name="ArrowRightOnRectangleIcon" size={16} />
                  <span>Sign In to Dashboard</span>
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400 font-medium">Demo Accounts</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Demo credentials table */}
          <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
              <p className="text-xs text-slate-500 font-medium">Click a row to autofill credentials</p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Role</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Access</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {DEMO_CREDENTIALS.map((cred) => (
                  <tr
                    key={`cred-${cred.role}`}
                    className="hover:bg-blue-50 cursor-pointer transition-colors border-b border-slate-100 last:border-0"
                    onClick={() => autofill(cred)}
                    title={`Autofill ${cred.role} credentials`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cred.badge === 'manager' ? 'bg-blue-500' : 'bg-slate-400'}`} />
                        <span className="font-medium text-slate-800 text-xs">{cred.role}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-slate-600 font-mono">{cred.email}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-slate-500">{cred.description}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-xs text-blue-600 font-semibold hover:text-blue-800 flex items-center gap-1 justify-end">
                        <Icon name="ArrowRightIcon" size={12} />
                        Use
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer note */}
          <p className="text-xs text-slate-400 text-center mt-6 leading-relaxed">
            By signing in, you agree to TallyDash's{' '}
            <button className="text-blue-600 hover:underline font-medium">Terms of Service</button>
            {' '}and{' '}
            <button className="text-blue-600 hover:underline font-medium">Privacy Policy</button>
          </p>
        </div>
      </div>
    </div>
  );
}